import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { api } from '../lib/api.js';

export default function Chores() {
  const { isOwner } = useOutletContext();
  const [list, setList] = useState(null);
  const [history, setHistory] = useState([]);
  const [err, setErr] = useState('');

  async function load() {
    try {
      const a = await api.get('/api/chores');
      setList(a.chores);
      const h = await api.get('/api/chores/history?days=7');
      setHistory(h.history);
    } catch (e) { setErr(e.message); }
  }
  useEffect(() => { load(); }, []);

  if (err) return <div className="text-rust-700 text-sm">{err}</div>;
  if (!list) return <div className="text-ink-700/60 italic">Loading…</div>;

  const active = list.filter(c => c.active);
  const inactive = list.filter(c => !c.active);

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-serif text-olive-900">Chores</h1>
        {isOwner && <Link to="/chores/new" className="btn-primary">+ Add chore</Link>}
      </header>

      {active.length === 0 ? (
        <div className="card text-center py-10">
          <p className="text-ink-700/70">No chores yet.</p>
          {isOwner && <Link to="/chores/new" className="btn-primary mt-4">Add first chore</Link>}
        </div>
      ) : (
        <ul className="space-y-2">
          {active.map(c => <ChoreRow key={c.id} c={c} isOwner={isOwner} reload={load} />)}
        </ul>
      )}

      {inactive.length > 0 && (
        <details>
          <summary className="text-sm text-ink-700/60 cursor-pointer">Show {inactive.length} inactive chore(s)</summary>
          <ul className="space-y-2 mt-2">
            {inactive.map(c => <ChoreRow key={c.id} c={c} isOwner={isOwner} reload={load} dim />)}
          </ul>
        </details>
      )}

      <section className="card">
        <h2 className="font-medium text-olive-900 mb-3">Last 7 days — completion log</h2>
        {history.length === 0 ? (
          <p className="text-sm text-ink-700/60 italic">No completions yet.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {history.slice(0, 30).map(h => (
              <li key={h.id} className="flex justify-between items-center border-l-2 border-olive-100 pl-3 py-1">
                <span>
                  <span className="font-medium">{h.chore_name}</span>
                  {h.ampm_slot && <span className="ml-1 pill bg-gold-500/15 text-gold-600">{h.ampm_slot}</span>}
                  {h.notes && <span className="text-ink-700/70 ml-2">— {h.notes}</span>}
                </span>
                <span className="text-xs text-ink-700/60">{h.date} · {h.user_name}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function ChoreRow({ c, isOwner, reload, dim }) {
  const cadence = formatCadence(c);
  return (
    <li className={`card ${dim ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-3">
        <CadenceBadge type={c.cadence_type} />
        <div className="flex-1">
          <div className="font-medium text-ink-900">{c.name}</div>
          {c.description && <div className="text-sm text-ink-700/70 mt-0.5">{c.description}</div>}
          <div className="text-xs text-ink-700/60 mt-1">
            {cadence} · <strong>{c.primary_assignee_name || '—'}</strong>
            {c.backup_assignee_name && <span> (backup: {c.backup_assignee_name})</span>}
            {c.segment_name && <span> · {c.segment_name}</span>}
          </div>
        </div>
        {isOwner && (
          <Link to={`/chores/${c.id}/edit`} className="text-xs text-olive-700 hover:underline">edit</Link>
        )}
      </div>
    </li>
  );
}

function CadenceBadge({ type }) {
  const map = {
    daily:      { label: 'Daily',  cls: 'bg-olive-100 text-olive-900' },
    daily_ampm: { label: 'AM+PM',  cls: 'bg-gold-500/15 text-gold-600' },
    weekday:    { label: 'Wkdy',   cls: 'bg-olive-100 text-olive-900' },
    weekly:     { label: 'Wkly',   cls: 'bg-olive-100 text-olive-900' },
    monthly:    { label: 'Mthly',  cls: 'bg-olive-100 text-olive-900' },
  };
  const m = map[type] || { label: type, cls: 'bg-ink-700/10 text-ink-700' };
  return <span className={`pill ${m.cls}`}>{m.label}</span>;
}

function formatCadence(c) {
  if (c.cadence_type === 'weekday' && c.cadence_detail) {
    try {
      const days = JSON.parse(c.cadence_detail);
      return days.map(d => d.slice(0, 3)).join(' / ');
    } catch { return c.cadence_type; }
  }
  return c.cadence_type.replace('_', ' ');
}
